import { useQuery } from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { ReportingSchemaService } from "@gen/tracker/v1/reporting_connect";
import {
  FormulaOp,
  MetricFormat,
  MetricKind,
  type DimensionDefinition,
  type MetricDefinition,
  type ReportFormula,
  type ReportView,
  type StatsRow,
} from "@gen/tracker/v1/reporting_pb";

import { DASH, formatInt, formatMoney, formatPct } from "@/lib/format";

const client = createClient(ReportingSchemaService);

export const reportingSchemaKeys = {
  all: ["reporting-schema"] as const,
};

/**
 * ReportingSchema is the workspace's metric/dimension catalog — the single
 * payload the SPA needs to render any report. Goal-generated metrics
 * (goal.<key>.count/…) arrive alongside the base traffic metrics; derived
 * metrics ship pre-topo-sorted so clients evaluate formulas in list order.
 */
export interface ReportingSchema {
  metrics: MetricDefinition[];
  metricsByKey: Map<string, MetricDefinition>;
  dimensions: DimensionDefinition[];
  presets: ReportView[];
  defaultViewId: string;
  primaryGoalKey: string;
  /** The workspace's own formulas, for their editor; their values come as metrics of category `custom`. */
  formulas?: ReportFormula[];
}

export function useReportingSchema() {
  return useQuery({
    queryKey: reportingSchemaKeys.all,
    staleTime: 30 * 60_000,
    queryFn: async (): Promise<ReportingSchema> => {
      const res = await client.getReportingSchema({});
      return {
        metrics: res.metrics,
        metricsByKey: new Map(res.metrics.map((m) => [m.key, m])),
        dimensions: res.dimensions,
        presets: res.presets,
        defaultViewId: res.defaultViewId,
        primaryGoalKey: res.primaryGoalKey,
        formulas: res.formulas,
      };
    },
  });
}

/**
 * formatMetric renders one StatsRow value according to its schema definition,
 * through the app's one formatter (`lib/format`). Absent / non-numeric values
 * render as "—" (not computable — e.g. a ratio with a zero denominator).
 *
 * Money is whole for totals and two decimals for a price per unit — a derived
 * RATIO in USD (EPC, CPA, CPC, CPL): «0,43» is a number, «0» is not. Percents
 * carry one decimal; ROI is signed and whole, the way the board prints it.
 */
export function formatMetric(
  def: MetricDefinition,
  value: string | number | undefined | null,
): string {
  if (value === undefined || value === null || value === "") return DASH;
  const n = typeof value === "number" ? value : Number.parseFloat(value);
  if (!Number.isFinite(n)) return DASH;
  switch (def.format) {
    case MetricFormat.INT:
      return formatInt(n);
    case MetricFormat.CURRENCY_USD:
      return formatMoney(n, { unit: isUnitPrice(def), signed: def.key === "profit" });
    case MetricFormat.PERCENT:
      return def.key === "roi" ? formatPct(n, { digits: 0, signed: true }) : formatPct(n);
    case MetricFormat.RATIO:
    case MetricFormat.DECIMAL:
    default:
      return formatMoney(n, { unit: true });
  }
}

/** A derived money ratio — revenue per click, cost per lead — is a price per unit. */
export function isUnitPrice(def: MetricDefinition): boolean {
  return def.kind === MetricKind.DERIVED && def.formula?.op === FormulaOp.RATIO;
}

/**
 * evaluateFormulas re-derives every DERIVED metric over a plain values bag —
 * used after client-side aggregation (report tree nodes / totals) where only
 * BASE_ADDITIVE keys were summed. Walks `metrics` in array order (the server
 * ships them pre-topo-sorted). RATIO with a zero denominator leaves the key
 * absent; missing operands count as 0 for DIFF/SUM; PRODUCT is absent when
 * any operand is. Same rules as internal/reporting evalFormula. Pure — returns a copy.
 */
export function evaluateFormulas(
  metrics: MetricDefinition[],
  values: Record<string, number>,
): Record<string, number> {
  const out: Record<string, number> = { ...values };
  for (const m of metrics) {
    if (m.kind !== MetricKind.DERIVED || !m.formula) continue;
    const ops = m.formula.operandKeys;
    switch (m.formula.op) {
      case FormulaOp.RATIO: {
        const num = out[ops[0] ?? ""] ?? 0;
        const den = out[ops[1] ?? ""] ?? 0;
        if (den === 0) delete out[m.key];
        else out[m.key] = num / den;
        break;
      }
      case FormulaOp.DIFF: {
        let acc = out[ops[0] ?? ""] ?? 0;
        for (let i = 1; i < ops.length; i++) acc -= out[ops[i]] ?? 0;
        out[m.key] = acc;
        break;
      }
      case FormulaOp.SUM: {
        let acc = 0;
        for (const k of ops) acc += out[k] ?? 0;
        out[m.key] = acc;
        break;
      }
      case FormulaOp.PRODUCT: {
        if (ops.length === 0 || ops.some((k) => out[k] === undefined)) {
          delete out[m.key];
          break;
        }
        out[m.key] = ops.reduce((acc, k) => acc * (out[k] ?? 0), 1);
        break;
      }
    }
  }
  return out;
}

/** StatsRow (decimal-string map) → numeric bag. Absent/NaN keys stay absent. */
export function statsRowToNumbers(row: StatsRow | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  if (!row) return out;
  for (const [k, v] of Object.entries(row.values)) {
    const n = Number.parseFloat(v);
    if (Number.isFinite(n)) out[k] = n;
  }
  return out;
}
